import { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, InputGroup, ToggleButton, ToggleButtonGroup, CloseButton } from 'react-bootstrap';

export default function SalesPaymentModal({
  show,
  onHide,
  onSave,
  defaultValues = {
    payingCustomer: '',
    invoiceNumber: '',
    invoiceDate: '',
    paymentType: 'PAID/CASH',
    amountPaid: ''
  }
}) {
  const [data, setData] = useState(defaultValues);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show) setData(defaultValues);
  }, [show, defaultValues]);

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setData((d) => ({ ...d, [field]: value }));
    setErrors((er) => ({ ...er, [field]: '' }));
  };

  const validate = () => {
    const er = {};
    if (!data.payingCustomer?.trim()) er.payingCustomer = 'Customer is required';
    if (!data.invoiceNumber?.trim()) er.invoiceNumber = 'Invoice number is required';
    if (!data.invoiceDate) er.invoiceDate = 'Invoice date is required';
    const amt = parseFloat(data.amountPaid);
    if (!(amt > 0)) er.amountPaid = 'Enter a valid amount';
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const handleSave = () => {
    if (validate()) onSave?.(data);
  };

  // Keyboard shortcuts: F2 save, ESC close, arrows toggle
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        handleSave();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        onHide?.();
      }
      if (['ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        setData((d) => ({
          ...d,
          paymentType: d.paymentType === 'PAID/CASH' ? 'UNPAID/CREDIT' : 'PAID/CASH'
        }));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show]);

  const payableAmt = useMemo(() => Number(data.amountPaid || 0), [data.amountPaid]);
  const payingNow = payableAmt;
  const balanceDue = '-';

  return (
    <Modal show={show} onHide={onHide} centered size="md" backdrop="static" contentClassName="shadow-lg">
      <Modal.Header className="border-0 pb-0">
        <div className="d-flex align-items-center w-100">
          <h5 className="mb-0 fw-semibold">Step 1/3: Add Payment</h5>
        </div>
      </Modal.Header>

      <Modal.Body className="pt-3">
        <p className="text-muted small mb-3">Fill in the details below to add</p>

        <Form>
          {/* Paying Customer */}
          <Form.Group className="mb-3">
            <Form.Label className="text-uppercase small fw-semibold">Paying Customer</Form.Label>
            <Form.Control
              placeholder="Enter customer name"
              value={data.payingCustomer}
              onChange={handleChange('payingCustomer')}
              isInvalid={!!errors.payingCustomer}
            />
            <Form.Control.Feedback type="invalid">{errors.payingCustomer}</Form.Control.Feedback>
          </Form.Group>

          {/* Invoice number + date */}
          <Row>
            <Form.Group as={Col} md="6" className="mb-3">
              <Form.Label className="text-uppercase small fw-semibold">Invoice Number</Form.Label>
              <Form.Control
                placeholder="Invoice/2"
                value={data.invoiceNumber}
                onChange={handleChange('invoiceNumber')}
                isInvalid={!!errors.invoiceNumber}
              />
              <Form.Control.Feedback type="invalid">{errors.invoiceNumber}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="6" className="mb-3">
              <Form.Label className="text-uppercase small fw-semibold">Invoice Date</Form.Label>
              <Form.Control
                type="date"
                value={data.invoiceDate}
                onChange={handleChange('invoiceDate')}
                isInvalid={!!errors.invoiceDate}
              />
              <Form.Control.Feedback type="invalid">{errors.invoiceDate}</Form.Control.Feedback>
            </Form.Group>
          </Row>

          {/* Payment type segmented control */}
          <Form.Group className="mb-3">
            <Form.Label className="text-uppercase small fw-semibold d-block">Customer Has</Form.Label>
            <div className="p-1 border rounded-3" style={{ borderColor: '#7c3aed' }}>
              <ToggleButtonGroup
                type="radio"
                name="paymentType"
                value={data.paymentType}
                onChange={(val) => handleChange('paymentType')(val)}
                className="w-100"
              >
                <ToggleButton
                  id="opt-credit"
                  value="UNPAID/CREDIT"
                  variant={data.paymentType === 'UNPAID/CREDIT' ? 'outline-secondary' : 'light'}
                  className="flex-fill"
                >
                  UNPAID/CREDIT
                </ToggleButton>
                <ToggleButton
                  id="opt-cash"
                  value="PAID/CASH"
                  variant={data.paymentType === 'PAID/CASH' ? 'outline-primary' : 'light'}
                  className="flex-fill"
                >
                  PAID/CASH
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
            <div className="small text-muted mt-2">
              Use Arrow Keys ← → to switch
            </div>
          </Form.Group>

          {/* Amount paid */}
          <Form.Group className="mb-3">
            <div className="d-flex align-items-center justify-content-between">
              <Form.Label className="text-uppercase small fw-semibold mb-0">Amount Paid</Form.Label>
              <span className="text-danger small">*Required</span>
            </div>
            <InputGroup hasValidation>
              <InputGroup.Text>₹</InputGroup.Text>
              <Form.Control
                inputMode="decimal"
                placeholder="181"
                value={data.amountPaid}
                onChange={handleChange('amountPaid')}
                isInvalid={!!errors.amountPaid}
              />
              <Form.Control.Feedback type="invalid">{errors.amountPaid}</Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Form>

        {/* Summary strip */}
        <div className="rounded-3 p-3 mt-2" style={{ background: '#fff7e6', border: '1px solid #ffe8bf' }}>
          <Row className="text-center">
            <Col>
              <div className="text-uppercase small text-muted">Payable Amt</div>
              <div className="fw-semibold">₹{payableAmt.toFixed(2)}</div>
            </Col>
            <Col>
              <div className="text-uppercase small text-muted">Paying Now</div>
              <div className="fw-semibold text-success">₹{payingNow.toFixed(2)}</div>
            </Col>
            <Col>
              <div className="text-uppercase small text-muted">Balance Due</div>
              <div className="fw-semibold">{balanceDue}</div>
            </Col>
          </Row>
        </div>

      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button variant="light" onClick={onHide}>Close</Button>
        <Button variant="success" onClick={handleSave}>Save & Continue</Button>
      </Modal.Footer>
    </Modal>
  );
}
